import { Component, Host, Prop, h, Event, EventEmitter, Watch, Method, State } from '@stencil/core';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';

/**
 * Componente switch per filtri con supporto per label e stato
 * @slot - Contenuto opzionale da mostrare accanto al switch (alternativo alla label)
 */
@Component({
  tag: 'jump-filter-switch',
  styleUrl: 'jump-filter-switch.scss',
  shadow: true,
})
export class JumpFilterSwitch {
  /** Il valore associato al switch */
  @Prop() value: string;

  /** La label da mostrare accanto al switch */
  @Prop() label: string;

  /** Nome identificativo del filtro, utilizzato negli eventi */
  @Prop() name: string = 'switch-filter';

  /** Stato del switch (acceso/spento) */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /** Stato del switch (disabilitato) */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Stato interno per tenere traccia dei valori selezionati */
  @State() values: string[] = [];

  /**
   * Watch sulla proprietà checked per reagire ai cambiamenti esterni
   */
  @Watch('checked')
  checkedChanged(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      // Aggiorna l'elemento sl-switch
      if (this.el) {
        this.el.checked = newValue;
      }

      // Aggiorna i valori
      this.updateValues();
    }
  }

  /** Riferimento all'elemento host */
  host: HTMLElement;

  /** Riferimento all'elemento sl-switch interno */
  el: any; // Usiamo any per evitare problemi di tipo con le proprietà specifiche di sl-switch

  componentDidLoad() {
    this.listenSLChange();
    // Inizializza i valori in base allo stato del checkbox
    this.updateValues();

    // Se è stato passato un valore di checked, applica lo stato all'elemento sl-switch
    if (this.el && this.checked) {
      this.el.checked = this.checked;
    }
  }

  /**
   * Aggiorna l'array dei valori in base allo stato del checkbox
   */
  private updateValues() {
    this.values = this.checked && this.value ? [this.value] : [];
  }

  /**
   * Ascolta gli eventi di cambio stato dal switch Shoelace
   */
  listenSLChange() {
    if (this.host) {
      this.host.addEventListener('sl-change', () => {
        // Ignora l'evento se lo switch è disabilitato
        if (this.disabled) {
          return;
        }

        this.checked = !this.checked;
        this.updateValues();

        // Evento specifico per il componente switch
        const switchEventData = {
          value: this.value,
          checked: this.checked
        };
        this.toggleCheckbox.emit(switchEventData);

        // Evento standard per il sistema di filtri
        const filterEventData = {
          name: this.name,
          values: this.values,
          checked: this.checked
        };
        this.toggleCheckboxFilterChange.emit(filterEventData);
      });
    }
  }

  /**
   * Metodo pubblico per impostare programmaticamente lo stato del switch
   * @param checked - Il nuovo stato del switch (true = acceso, false = spento)
   * @param emitEvent - Se emettere eventi di cambio stato (default: true)
   * @returns {Promise<boolean>} Il nuovo stato del switch
   */
  @Method()
  async setValue(checked: boolean, emitEvent: boolean = true): Promise<boolean> {
    // Se il component è disabilitato, non fare nulla
    if (this.disabled) {
      return this.checked;
    }

    // Salva il valore precedente per l'evento
    const oldValue = this.checked;

    // Imposta il nuovo valore
    this.checked = checked;

    // Aggiorna l'elemento sl-switch interno
    if (this.el) {
      this.el.checked = checked;
    }

    // Aggiorna l'array dei valori
    this.updateValues();

    // Emetti eventi se richiesto
    if (emitEvent && oldValue !== checked) {
      // Evento specifico per il componente switch
      const switchEventData = {
        value: this.value,
        checked: this.checked,
        programmatic: true
      };
      this.toggleCheckbox.emit(switchEventData);

      // Evento standard per il sistema di filtri
      const filterEventData = {
        name: this.name,
        values: this.values,
        checked: this.checked,
        programmatic: true
      };
      this.toggleCheckboxFilterChange.emit(filterEventData);

      // Evento specifico per cambio valore programmato
      this.valueChange.emit({
        value: this.value,
        checked: this.checked,
        oldChecked: oldValue
      });
    }

    return this.checked;
  }

  /**
   * Evento emesso quando cambia lo stato del switch
   */
  @Event({ eventName: 'jump-switch-change' }) toggleCheckbox: EventEmitter;
  @Event({ eventName: 'jump-filterchange' }) toggleCheckboxFilterChange: EventEmitter;
  /**
   * Evento emesso quando il valore viene cambiato programmaticamente
   */
  @Event({ eventName: 'jump-switch-value-change' }) valueChange: EventEmitter;

  /**
   * Metodo pubblico per verificare lo stato corrente del switch
   * @returns {boolean} Lo stato corrente del switch (true = acceso, false = spento)
   */
  @Method()
  async isChecked() {
    return this.checked;
  }

  /**
   * Metodo pubblico per ottenere i valori selezionati
   * @returns {string[]} Array contenente il valore se selezionato, altrimenti array vuoto
   */
  @Method()
  async getValues() {
    return this.values;
  }

  /**
   * Metodo pubblico per ottenere il nome del filtro
   * @returns {string} Il nome del filtro
   */
  @Method()
  async getName() {
    return this.name;
  }

  render() {
    return (
      <Host ref={(host: HTMLElement) => (this.host = host)}>
        {this.value && this.label && (
          <sl-switch
            value={this.value}
            ref={(el: any) => (this.el = el)}
            checked={this.checked}
            disabled={this.disabled}
          >
            {this.label}
          </sl-switch>
        )}
      </Host>
    );
  }
}
